import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTheme } from "@mui/material/styles";

import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { TenantContext } from "context/AppProvider/TenantContext";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import Overview from "./components/Overview";
import defaultCompanyLogo from "assets/images/defaultCompany.svg";
import { PERMISSIONS } from "constants/portal";
import { THEME_COLORS } from "constants/theme";
import UploadModal from "layouts/pages/account/components/Header/components/UploadModal";
import RemovePictureModal from "layouts/pages/account/components/Header/components/RemovePictureModal";

function Company() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { tenantData, tenantLogoKey } = useContext(TenantContext);
  const { userPermissions } = useContext(PermissionsContext);
  const { pathname } = useLocation();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [logoMenuAnchor, setLogoMenuAnchor] = useState(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openRemovePictureModal, setOpenRemovePictureModal] = useState(false);
  const canEditCompany = Boolean(
    userPermissions?.find((permission) => permission.name === PERMISSIONS.ACCESS_TO_COMPANY_EDIT_PAGE)?.value
  );

  useEffect(() => {
    setCompanyLogo(tenantData?.logo_url?.trim()
      ? `${tenantData.logo_url}${tenantData.logo_url.includes("?") ? "&" : "?"}timestamp=${Date.now()}`
      : null);
  }, [tenantData?.logo_url, tenantLogoKey]);

  const closeLogoMenu = () => setLogoMenuAnchor(null);

  return (
    <>
      <SoftBox sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
        <DashboardNavbar title="Company" flush />
      </SoftBox>

      <DashboardLayout flush>
        <SoftBox
          sx={{
            flex: 1,
            minHeight: "calc(100vh - 58px)",
            backgroundColor: isDarkMode ? THEME_COLORS.DARK.PRIMARY_BG : THEME_COLORS.LIGHT.PRIMARY_BG,
          }}
        >
          <SoftBox
            sx={{
              width: "100%",
              maxWidth: "1600px",
              mx: "auto",
              px: { xs: 2, sm: 3, lg: 4 },
              pt: { xs: 3, lg: 4 },
              pb: 5,
            }}
          >
            <SoftBox
              display="flex"
              alignItems="center"
              sx={{
                flexWrap: "wrap",
                gap: { xs: 2, sm: 3 },
                pb: { xs: 3, lg: 4 },
                mb: { xs: 3, lg: 4 },
                borderBottom: "1px solid rgba(140, 150, 170, 0.18)",
              }}
            >
              <SoftBox position="relative" sx={{ flexShrink: 0 }}>
                <SoftBox
                  component="img"
                  src={companyLogo || defaultCompanyLogo}
                  alt={`${tenantData?.company_name || "Company"} logo`}
                  sx={{
                    display: "block",
                    width: { xs: 84, sm: 112 },
                    height: { xs: 84, sm: 112 },
                    objectFit: "contain",
                    borderRadius: "8px",
                    border: "1px solid rgba(140, 150, 170, 0.3)",
                    backgroundColor: isDarkMode ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
                  }}
                />
                <IconButton
                  aria-label="Edit company logo"
                  title={canEditCompany ? "Edit company logo" : "You do not have permission to edit the company"}
                  disabled={!canEditCompany}
                  onClick={(event) => setLogoMenuAnchor(event.currentTarget)}
                  size="small"
                  sx={{
                    position: "absolute",
                    right: -8,
                    bottom: -8,
                    zIndex: 1,
                    width: 36,
                    height: 36,
                    backgroundColor: isDarkMode ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
                    color: isDarkMode ? "#aa8bfd" : "#7443ff",
                    border: "2px solid #7443ff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                    "&.Mui-disabled": { color: "#b8a8ef", opacity: 1 },
                    "&:hover": { backgroundColor: isDarkMode ? THEME_COLORS.DARK.ACTIVE_NAVBAR_BG : "#f0ecff" },
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </SoftBox>

              <SoftBox sx={{ minWidth: 0, flex: { xs: "1 1 calc(100% - 110px)", sm: "1 1 auto" } }}>
                <SoftTypography
                  variant="overline"
                  sx={{ color: isDarkMode ? "#aa8bfd" : "#7443ff", letterSpacing: "0.08em" }}
                >
                  Company
                </SoftTypography>
                <SoftTypography
                  variant="h3"
                  fontWeight="bold"
                  className="white-to-dark"
                  sx={{ lineHeight: 1.15, overflowWrap: "anywhere" }}
                >
                  {tenantData?.company_name || "Company"}
                </SoftTypography>
                <SoftTypography
                  variant="button"
                  color="text"
                  sx={{ display: "block", mt: 1, overflowWrap: "anywhere" }}
                >
                  Company ID&nbsp; {tenantData?.tenant_id || "—"}
                </SoftTypography>
              </SoftBox>

              {!isEditing && (
                <SoftButton
                  variant="outlined"
                  color="primary"
                  size="medium"
                  disabled={!canEditCompany}
                  title={canEditCompany ? "Edit company" : "You do not have permission to edit the company"}
                  onClick={() => setIsEditing(true)}
                  sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
                >
                  Edit company
                </SoftButton>
              )}
            </SoftBox>

            <Overview isEditing={isEditing} setIsEditing={setIsEditing} />
          </SoftBox>
        </SoftBox>
      </DashboardLayout>

      <Menu anchorEl={logoMenuAnchor} open={Boolean(logoMenuAnchor)} onClose={closeLogoMenu}>
        <MenuItem onClick={() => { closeLogoMenu(); setOpenUploadModal(true); }}>
          {tenantData?.logo_url?.trim() ? "Replace logo" : "Upload logo"}
        </MenuItem>
        {tenantData?.logo_url?.trim() && (
          <MenuItem onClick={() => { closeLogoMenu(); setOpenRemovePictureModal(true); }}>
            Remove logo
          </MenuItem>
        )}
      </Menu>
      <UploadModal
        openUploadModal={openUploadModal}
        setOpenUploadModal={setOpenUploadModal}
        setCompanyLogo={setCompanyLogo}
        pathname={pathname}
      />
      <RemovePictureModal
        openRemovePictureModal={openRemovePictureModal}
        setOpenRemovePictureModal={setOpenRemovePictureModal}
        setCompanyLogo={setCompanyLogo}
      />
    </>
  );
}

export default Company;
